import { StatusChip } from "$components/chip";
import {
  Table,
  type TableColumnDefinition,
  type TableRowDefinition,
} from "$components/table";
import { makeStyles, Typography } from "@material-ui/core";
import { FormattedMessage, useIntl } from "react-intl";
import {
  type ReceiveBulkRowValidation,
  type ResolvedSku,
  RowSkuPicker,
} from "./row-sku-picker";

const useStyles = makeStyles((theme) => ({
  errorRow: {
    backgroundColor: theme.palette.error.light,
  },
}));

interface Props {
  rows: readonly ReceiveBulkRowValidation[];
  resolutions: Record<number, ResolvedSku>;
  onResolve: (rowIndex: number, sku: ResolvedSku | null) => void;
  unknownLocations: Record<number, string | undefined>;
}

const RowStatus: FC<{
  row: ReceiveBulkRowValidation;
  resolved: boolean;
  unknownLocation?: string;
}> = ({ row, resolved, unknownLocation }) => {
  if (row.hasError || unknownLocation) {
    return (
      <StatusChip
        status="invalid"
        label={
          row.hasError ? (
            (row.errorMessage ?? (
              <FormattedMessage
                id="inventory.bulkReceive.status.error"
                defaultMessage="Error"
              />
            ))
          ) : (
            <FormattedMessage
              id="inventory.bulkReceive.status.unknownLocation"
              defaultMessage='No location named "{name}"'
              values={{ name: unknownLocation }}
            />
          )
        }
      />
    );
  }

  if (resolved) {
    return (
      <StatusChip
        status="valid"
        label={
          <FormattedMessage
            id="inventory.bulkReceive.status.resolved"
            defaultMessage="Ready"
          />
        }
      />
    );
  }

  return (
    <StatusChip
      status="disabled"
      label={
        <FormattedMessage
          id="inventory.bulkReceive.status.unmatched"
          defaultMessage="Find product"
        />
      }
    />
  );
};

export const ReceiveBulkPreviewTable: FC<Props> = ({
  rows,
  resolutions,
  onResolve,
  unknownLocations,
}) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();

  const columns: TableColumnDefinition[] = [
    {
      field: "skuName",
      headerName: formatMessage({
        id: "inventory.bulkReceive.column.uploadedName",
        defaultMessage: "Uploaded name",
      }),
      flex: 1.5,
    },
    {
      field: "sku",
      headerName: formatMessage({
        id: "inventory.bulkReceive.column.matchedSku",
        defaultMessage: "Matched product",
      }),
      flex: 2,
      renderCell: (_value, _index, _col, row) => {
        const original = row.original as ReceiveBulkRowValidation;
        return (
          <RowSkuPicker
            row={original}
            resolved={resolutions[original.rowIndex] ?? null}
            onResolve={(sku) => onResolve(original.rowIndex, sku)}
          />
        );
      },
    },
    {
      field: "quantity",
      headerName: formatMessage({
        id: "inventory.bulkReceive.column.quantity",
        defaultMessage: "Quantity",
      }),
      flex: 0.7,
      alignContents: "right",
    },
    {
      field: "expiryDate",
      headerName: formatMessage({
        id: "inventory.bulkReceive.column.expiry",
        defaultMessage: "Expiry",
      }),
      flex: 1,
    },
    {
      field: "locationName",
      headerName: formatMessage({
        id: "inventory.bulkReceive.column.location",
        defaultMessage: "Location",
      }),
      flex: 1,
      renderCell: (_value, _index, _col, row) => {
        const original = row.original as ReceiveBulkRowValidation;
        const unknown = unknownLocations[original.rowIndex];
        return (
          <Typography variant="body2" color={unknown ? "error" : "textPrimary"}>
            {original.locationName ?? ""}
          </Typography>
        );
      },
    },
    {
      field: "status",
      headerName: formatMessage({
        id: "inventory.bulkReceive.column.status",
        defaultMessage: "Status",
      }),
      flex: 1,
      renderCell: (_value, _index, _col, row) => {
        const original = row.original as ReceiveBulkRowValidation;
        return (
          <RowStatus
            row={original}
            resolved={!!resolutions[original.rowIndex]}
            unknownLocation={unknownLocations[original.rowIndex]}
          />
        );
      },
    },
  ];

  const tableRows: TableRowDefinition[] = rows.map((row) => ({
    id: row.rowIndex,
    original: row,
    skuName: row.skuName ?? "",
    quantity: row.quantity ?? "",
    expiryDate: row.expiryDate ?? "",
    locationName: row.locationName ?? "",
  }));

  return (
    <Table
      columns={columns}
      rows={tableRows}
      dataTestId="bulk-receive-preview-table"
      classes={{
        rowClass: (row) => {
          const original = row.original as ReceiveBulkRowValidation;
          return original.hasError || unknownLocations[original.rowIndex]
            ? classes.errorRow
            : "";
        },
      }}
    />
  );
};
